import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

const ENV = (window as any).ENV ?? {};

const normalizeUrl = (url: string): string =>
  url.endsWith('/api') ? url : `${url}/api`;

const DEFAULT_URL = normalizeUrl(
  ENV.REACT_APP_API_URL || process.env.REACT_APP_API_URL || '/api'
);

const UPLOAD_URL = normalizeUrl(
  ENV.REACT_APP_API_URL_UPLOAD || process.env.REACT_APP_API_URL_UPLOAD || DEFAULT_URL.replace(/\/api$/, '')
);

const SEARCH_URL = normalizeUrl(
  ENV.REACT_APP_API_URL_SEARCH || process.env.REACT_APP_API_URL_SEARCH || DEFAULT_URL.replace(/\/api$/, '')
);

console.log('🔗 API URLs configuradas:', { default: DEFAULT_URL, upload: UPLOAD_URL, search: SEARCH_URL });

export const getImageUrl = (imageUrl?: string): string => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;
  const baseUrl = DEFAULT_URL.endsWith('/api') ? DEFAULT_URL.slice(0, -4) : DEFAULT_URL;
  if (imageUrl.startsWith('/images/')) return `${baseUrl}${imageUrl}`;
  return `${DEFAULT_URL}${imageUrl}`;
};

interface AuthAxiosRequestConfig extends AxiosRequestConfig {
  skipAuthRedirect?: boolean;
}

const handleUnauthorized = () => {
  localStorage.removeItem('access_token');
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

const shouldSkipRedirect = (config?: AuthAxiosRequestConfig): boolean => {
  if (!config) return false;
  const headerSkip = (config.headers as any)?.['X-Skip-Auth-Redirect'];
  return Boolean(config.skipAuthRedirect || headerSkip);
};

const makeInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use((config) => {
    const cfg = config as AuthAxiosRequestConfig;
    const token = localStorage.getItem('access_token');
    if (token) {
      cfg.headers = cfg.headers ?? {};
      (cfg.headers as any)['Authorization'] = `Bearer ${token}`;
    }
    return cfg as any;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const skipRedirect = shouldSkipRedirect(error?.config as AuthAxiosRequestConfig);
      if (status === 401 && !skipRedirect) {
        handleUnauthorized();
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const api = makeInstance(DEFAULT_URL);
const uploadApi = makeInstance(UPLOAD_URL);
const searchApi = makeInstance(SEARCH_URL);

export const authAPI = {
  verify: () => api.post(
    '/tokens/verify',
    undefined,
    { skipAuthRedirect: true, headers: { 'X-Skip-Auth-Redirect': '1' } } as AuthAxiosRequestConfig
  ),
  validateFace: (file: Blob) => {
    const formData = new FormData();
    formData.append('file', file, 'cropped.jpg');
    return api.post('/people/validate-face', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const instanceAPI = {
  list: () => api.get('/instances'),
  get: (id: string) => api.get(`/instances/${id}`),
  create: (data: any) => api.post('/instances', data),
  update: (id: string, data: any) => api.put(`/instances/${id}`, data),
  delete: (id: string) => api.delete(`/instances/${id}`),
};

export const organizationAPI = {
  list: (sortBy: string = 'name', sortOrder: string = 'asc') => 
    api.get('/organizations', { params: { sort_by: sortBy, sort_order: sortOrder } }),
  get: (id: string) => api.get(`/organizations/${id}`),
  create: (data: any) => api.post('/organizations', data),
  update: (id: string, data: any) => api.put(`/organizations/${id}`, data),
  delete: (id: string) => api.delete(`/organizations/${id}`),
};

export const tokenAPI = {
  list: (sortBy: string = 'created_at', sortOrder: string = 'desc') =>
    api.get('/tokens', { params: { sort_by: sortBy, sort_order: sortOrder } }),
  get: (id: string) => api.get(`/tokens/${id}`),
  first: () => api.get('/tokens/first', { skipAuthRedirect: true } as AuthAxiosRequestConfig),
  create: (data: any) => api.post('/tokens', data),
  update: (id: string, data: any) => api.put(`/tokens/${id}`, data),
  delete: (id: string) => api.delete(`/tokens/${id}`),
};

export const peopleAPI = {
  list: (organizationId: string, sortBy: string = 'created_at', sortOrder: string = 'desc') =>
    api.get(`/organizations/${organizationId}/people`, { params: { sort_by: sortBy, sort_order: sortOrder } }),
  get: (organizationId: string, id: string) => api.get(`/organizations/${organizationId}/people/${id}`),
  create: (organizationId: string, data: any) => api.post(`/organizations/${organizationId}/people`, data),
  update: (organizationId: string, id: string, data: any) => api.put(`/organizations/${organizationId}/people/${id}`, data),
  delete: (organizationId: string, id: string) => api.delete(`/organizations/${organizationId}/people/${id}`),
  uploadImage: (organizationId: string, personId: string, file: File, setAsMain: boolean = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return uploadApi.post(`/organizations/${organizationId}/people/${personId}/upload-image`, formData, {
      params: { set_as_main: setAsMain },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  setMainImage: (organizationId: string, personId: string, imageUrl: string) =>
    api.post(`/organizations/${organizationId}/people/${personId}/set-main-image`, undefined, {
      params: { image_url: imageUrl },
    }),
  deleteImage: (organizationId: string, personId: string, filename: string) =>
    api.delete(`/organizations/${organizationId}/people/${personId}/images/${filename}`),
  replaceImage: (personId: string, file: Blob, imageIndex: number) => {
    const formData = new FormData();
    formData.append('file', file, `cropped_${Date.now()}.jpg`);
    return uploadApi.post(`/people/${personId}/replace-image`, formData, {
      params: { image_index: imageIndex },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const searchAPI = {
  search: (file: File, organizationId: string, personId?: string, detector?: string, validation: boolean = false) => {
    const formData = new FormData();
    formData.append('file', file);
    const params: any = { organization_id: organizationId };
    if (personId) params.person_id = personId;
    if (detector) params.detector = detector;
    if (validation !== undefined) params.validation = validation;
    return searchApi.post('/search', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
  },
  searchByImageId: (queryImageId: string, organizationId: string, personId?: string, detector?: string, validation: boolean = false) => {
    const params: any = {
      query_image_id: queryImageId,
      organization_id: organizationId,
      validation: validation,
    };
    if (personId) params.person_id = personId;
    if (detector) params.detector = detector;
    return searchApi.post('/search', null, { params });
  },
};

export default api;
